import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Check, Copy, PackageCheck, PhoneCall, Truck, Home, MapPin, Wallet, Eye, ShoppingBag, ReceiptText, ChevronLeft, Sparkles,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { formatPrice, cn, itemMeasurements } from '@/lib/utils';
import ProductImage from '@/components/ProductImage';
import { playSuccessChime } from '@/lib/successChime';
import { Skeleton } from '@/components/ui/skeleton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { icon: Check, title: 'تم استلام الطلب', text: 'طلبك وصلنا بنجاح' },
  { icon: PhoneCall, title: 'تأكيد الطلب', text: 'رح نتواصل معكِ للتأكيد' },
  { icon: PackageCheck, title: 'التجهيز', text: 'نغلّف طلبك بعناية' },
  { icon: Truck, title: 'التوصيل', text: 'يوصلك لباب البيت' },
];

// قصاصات احتفال ذهبية/عنابية (CSS فقط، بتظهر مرة واحدة)
const Confetti = () => {
  const pieces = useMemo(
    () =>
      Array.from({ length: 42 }, (_, i) => ({
        left: Math.random() * 100,
        delay: Math.random() * 0.9,
        duration: 2.6 + Math.random() * 1.8,
        size: 6 + Math.random() * 7,
        rotate: Math.random() * 360,
        color: ['#C9A24B', '#E8C872', '#722F37', '#F4D58D', '#9E4A55'][i % 5],
        round: i % 3 === 0,
      })),
    []
  );
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
      {pieces.map((p, i) => (
        <span
          key={i}
          className="success-confetti"
          style={{
            left: `${p.left}%`,
            width: p.size,
            height: p.round ? p.size : p.size * 0.45,
            background: p.color,
            borderRadius: p.round ? '9999px' : '2px',
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
            '--r': `${p.rotate}deg`,
          }}
        />
      ))}
    </div>
  );
};

const AnimatedCheck = () => (
  <div className="relative mx-auto w-28 h-28 md:w-32 md:h-32">
    <span className="absolute inset-0 rounded-full bg-brand-gold/30 success-ring" />
    <span className="absolute inset-0 rounded-full bg-brand-gold/20 success-ring [animation-delay:0.6s]" />
    <div className="relative w-full h-full rounded-full bg-gradient-to-br from-burgundy-500 to-burgundy-600 shadow-2xl shadow-burgundy-500/40 ring-4 ring-white flex items-center justify-center success-pop">
      <svg viewBox="0 0 52 52" className="w-14 h-14 md:w-16 md:h-16">
        <path className="success-check" fill="none" stroke="#F4D58D" strokeWidth="4.5" strokeLinecap="round" strokeLinejoin="round" d="M14 27l8 8 16-17" />
      </svg>
    </div>
    <Sparkles className="absolute -top-1 -left-2 w-6 h-6 text-brand-gold success-twinkle" />
    <Sparkles className="absolute bottom-1 -right-3 w-5 h-5 text-brand-gold success-twinkle [animation-delay:0.8s]" />
  </div>
);

const OrderSuccess = () => {
  const { orderNumber } = useParams();
  const location = useLocation();
  const { user, token } = useAuth();
  const [order, setOrder] = useState(location.state?.order ?? null);
  const [notFound, setNotFound] = useState(false);

  // لو الصفحة اتفتحت مباشرة (ريفريش/رابط) نجيب الطلب من السيرفر
  useEffect(() => {
    if (order || !token) return;
    axios
      .get(`${API}/orders/${orderNumber}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setOrder(res.data?.data))
      .catch(() => setNotFound(true));
  }, [orderNumber, token]);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  // نغمة التأكيد: بس لما العميلة توصل هنا من إتمام الطلب (مش ريفريش أو رابط)، ومرة واحدة لكل طلب
  useEffect(() => {
    if (!location.state?.order) return;
    const key = `order_chime:${orderNumber}`;
    try {
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, '1');
    } catch {
      /* التخزين غير متاح — نشغّلها عادي */
    }
    playSuccessChime();
  }, [orderNumber, location.state]);

  const copyNumber = () => {
    navigator.clipboard?.writeText(orderNumber);
    toast.success('تم نسخ رقم الطلب');
  };

  const firstName = (user?.full_name || user?.name || '').trim().split(/\s+/)[0];
  const address = order?.shipping_address;

  if (notFound) {
    return (
      <div className="py-24 text-center font-cairo">
        <p className="text-gray-600 mb-6">لم نجد هذا الطلب.</p>
        <Link to="/account?tab=orders" className="text-burgundy-500 font-bold hover:underline">عرض طلباتي</Link>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-[radial-gradient(ellipse_at_top,rgba(201,162,75,0.18),transparent_55%),radial-gradient(ellipse_at_bottom,rgba(114,47,55,0.10),transparent_60%)] pb-16">
      <Confetti />

      <div className="relative container mx-auto px-4 sm:px-6 lg:px-8 max-w-[1100px] pt-10 md:pt-16">
        {/* البطاقة الرئيسية */}
        <section className="relative text-center rounded-[2rem] bg-white/90 dark:bg-white/5 backdrop-blur-xl border border-brand-gold/25 shadow-[0_30px_80px_-30px_rgba(114,47,55,0.35)] px-6 py-10 md:px-14 md:py-14 success-rise">
          <div className="absolute inset-x-10 top-0 h-px bg-gradient-to-l from-transparent via-brand-gold to-transparent" />

          <AnimatedCheck />

          <p className="mt-8 text-xs md:text-sm tracking-[0.3em] text-brand-gold font-bold font-cairo">WAHIBA FASHION</p>
          <h1 className="mt-3 text-3xl md:text-5xl font-tajawal font-bold text-brand-black leading-tight">
            {firstName ? `شكراً لكِ يا ${firstName}` : 'شكراً لكِ'}
          </h1>
          <p className="mt-3 text-base md:text-xl text-gray-600 font-cairo">
            تم استلام طلبك بنجاح، وسنتواصل معكِ قريباً لتأكيده
          </p>

          <div className="mt-8 inline-flex items-center justify-center gap-2 sm:gap-3 rounded-2xl bg-gradient-to-l from-brand-gold/15 via-brand-gold/5 to-brand-gold/15 border border-brand-gold/30 px-4 sm:px-5 py-3">
            <span className="text-sm text-gray-500 font-cairo">رقم الطلب</span>
            <span className="font-mono text-base sm:text-lg md:text-2xl font-bold tracking-wider text-burgundy-600" dir="ltr">{orderNumber}</span>
            <button
              type="button"
              onClick={copyNumber}
              className="w-9 h-9 rounded-full bg-white shadow-sm ring-1 ring-brand-gold/30 flex items-center justify-center text-gray-500 hover:text-burgundy-500 hover:scale-105 transition"
              aria-label="نسخ رقم الطلب"
            >
              <Copy className="w-4 h-4" />
            </button>
          </div>

          {/* مراحل الطلب */}
          <ol className="mt-10 md:mt-12 grid grid-cols-2 md:grid-cols-4 gap-y-8 gap-x-4 max-w-3xl mx-auto">
            {STEPS.map((step, i) => {
              const done = i === 0;
              const Icon = step.icon;
              return (
                <li key={step.title} className="relative flex flex-col items-center text-center success-rise" style={{ animationDelay: `${0.35 + i * 0.12}s` }}>
                  {i < STEPS.length - 1 && (
                    <span className="hidden md:block absolute top-6 right-[calc(50%+28px)] w-[calc(100%-56px)] h-0.5 bg-gradient-to-l from-brand-gold/60 to-gray-200" />
                  )}
                  <span
                    className={cn(
                      'relative w-12 h-12 rounded-full flex items-center justify-center transition',
                      done
                        ? 'bg-burgundy-500 text-white shadow-lg shadow-burgundy-500/30 ring-4 ring-burgundy-500/15'
                        : 'bg-white text-gray-400 ring-1 ring-gray-200'
                    )}
                  >
                    <Icon className="w-5 h-5" />
                  </span>
                  <p className={cn('mt-3 font-tajawal font-bold text-sm md:text-base', done ? 'text-burgundy-600' : 'text-brand-black')}>{step.title}</p>
                  <p className="text-xs text-gray-500 font-cairo mt-0.5">{step.text}</p>
                </li>
              );
            })}
          </ol>

          <div className="mt-10 md:mt-12 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/products"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 h-12 px-8 rounded-full bg-burgundy-500 hover:bg-burgundy-600 text-white font-cairo font-bold shadow-lg shadow-burgundy-500/25 transition hover:-translate-y-0.5"
            >
              <ShoppingBag className="w-5 h-5" />
              متابعة التسوق
            </Link>
            <Link
              to="/account?tab=orders"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 h-12 px-8 rounded-full border-2 border-brand-gold/60 text-brand-black hover:bg-brand-gold/10 font-cairo font-bold transition"
            >
              <ReceiptText className="w-5 h-5 text-brand-gold" />
              عرض طلباتي
            </Link>
          </div>
        </section>

        {/* تفاصيل الطلب */}
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-5 gap-6 success-rise" style={{ animationDelay: '0.5s' }}>
          <section className="lg:col-span-3 rounded-3xl bg-white dark:bg-white/5 border border-gray-200/70 shadow-sm p-5 md:p-7">
            <h2 className="font-tajawal font-bold text-xl text-brand-black mb-5 flex items-center gap-2">
              <ShoppingBag className="w-5 h-5 text-burgundy-500" />
              ملخص الطلب
              {order && <span className="text-sm font-cairo font-normal text-gray-500">({order.items_count} قطعة)</span>}
            </h2>

            {!order ? (
              <div className="space-y-4">
                {[0, 1].map((i) => (
                  <div key={i} className="flex gap-4">
                    <Skeleton className="w-16 h-20 rounded-xl" />
                    <div className="flex-1 space-y-2"><Skeleton className="h-4 w-1/2" /><Skeleton className="h-3 w-1/3" /></div>
                  </div>
                ))}
              </div>
            ) : (
              <>
                <ul className="divide-y divide-gray-100">
                  {order.items.map((item) => (
                    <li key={item.id} className="flex gap-4 py-4 first:pt-0">
                      <ProductImage
                        src={item.image_url}
                        name={item.name_ar}
                        className="w-16 h-20 shrink-0 rounded-xl object-cover ring-1 ring-gray-100"
                        textClassName="text-[10px]"
                      />
                      <div className="flex-1 min-w-0 font-cairo">
                        <p className="font-semibold text-brand-black truncate">{item.name_ar}</p>
                        {item.options_label && <p className="text-xs text-gray-500 mt-0.5">{item.options_label}</p>}
                        {itemMeasurements(item).length > 0 && (
                          <p className="text-xs text-burgundy-500 mt-0.5">
                            {itemMeasurements(item).map((m) => `${m.name} ${m.value}`).join(' · ')}
                          </p>
                        )}
                        <div className="mt-2 flex items-center justify-between gap-2">
                          <p className="text-xs text-gray-500">{formatPrice(item.unit_price)} × {item.quantity}</p>
                          <p className="font-bold text-burgundy-500 whitespace-nowrap">{formatPrice(item.line_total)}</p>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>

                <div className="mt-4 pt-4 border-t border-dashed border-gray-200 space-y-2 font-cairo text-sm">
                  <div className="flex justify-between text-gray-600"><span>المجموع الفرعي</span><span>{formatPrice(order.subtotal)}</span></div>
                  <div className="flex justify-between text-gray-600"><span>رسوم التوصيل</span><span>{formatPrice(order.delivery_fee)}</span></div>
                  <div className="flex justify-between items-center pt-3 mt-1 border-t border-gray-100">
                    <span className="font-tajawal font-bold text-lg text-brand-black">الإجمالي</span>
                    <span className="font-bold text-2xl text-burgundy-500">{formatPrice(order.total)}</span>
                  </div>
                </div>
              </>
            )}
          </section>

          <aside className="lg:col-span-2 space-y-4">
            <section className="rounded-3xl bg-white dark:bg-white/5 border border-gray-200/70 shadow-sm p-5 md:p-6">
              <h2 className="font-tajawal font-bold text-lg text-brand-black mb-4 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-burgundy-500" />
                عنوان التوصيل
              </h2>
              {address ? (
                <div className="font-cairo text-sm text-gray-700 space-y-1 leading-relaxed">
                  {address.name && <p className="font-bold text-brand-black">{address.name}</p>}
                  <p>{[address.city, address.street].filter(Boolean).join('، ')}</p>
                  {address.building && <p className="text-gray-500">{address.building}</p>}
                  {address.details && <p className="text-gray-500">{address.details}</p>}
                  {address.phone && <p className="text-gray-500" dir="ltr" style={{ textAlign: 'right' }}>{address.phone}</p>}
                </div>
              ) : (
                <div className="space-y-2"><Skeleton className="h-4 w-2/3" /><Skeleton className="h-3 w-1/2" /></div>
              )}
            </section>

            <section className="rounded-3xl bg-white dark:bg-white/5 border border-gray-200/70 shadow-sm p-5 md:p-6 space-y-4 font-cairo text-sm">
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center"><Wallet className="w-5 h-5" /></span>
                <div>
                  <p className="font-bold text-brand-black">الدفع عند الاستلام</p>
                  <p className="text-xs text-gray-500">جهّزي {order ? formatPrice(order.total) : 'المبلغ'} عند وصول المندوب</p>
                </div>
              </div>
              {order?.preview_service_requested && (
                <div className="flex items-center gap-3">
                  <span className="w-10 h-10 rounded-full bg-brand-gold/15 text-brand-gold flex items-center justify-center"><Eye className="w-5 h-5" /></span>
                  <div>
                    <p className="font-bold text-brand-black">خدمة المعاينة قبل الدفع</p>
                    <p className="text-xs text-gray-500">تقدرين تعاينين المنتج قبل الدفع</p>
                  </div>
                </div>
              )}
              {order?.notes && (
                <div className="rounded-2xl bg-gray-50 p-3 text-gray-600">
                  <p className="text-xs font-bold text-gray-500 mb-1">ملاحظاتك</p>
                  {order.notes}
                </div>
              )}
            </section>

            <Link
              to="/"
              className="flex items-center justify-between rounded-3xl bg-gradient-to-l from-burgundy-600 to-burgundy-500 text-white p-5 shadow-lg shadow-burgundy-500/20 hover:-translate-y-0.5 transition"
            >
              <span className="flex items-center gap-3 font-cairo font-bold">
                <Home className="w-5 h-5 text-brand-gold" />
                العودة للرئيسية
              </span>
              <ChevronLeft className="w-5 h-5" />
            </Link>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default OrderSuccess;
